<template>
  <div>
    <el-row :gutter="35" class="info-boxes" type="flex">
      <el-col :xs="24" :md="12">
        <section class="boxes-group-header">
          <h2>
            {{ $t($i18nLabelsHelper(organizationType, `charts.${screen}.performance_title`)) }}
          </h2>
        </section>
      </el-col>
    </el-row>
    <el-row :gutter="35" class="info-boxes chart-section" type="flex" id="org-performance">
      <el-col class="box" :xs="24" :md="12">
        <elsa-section :title="$t($i18nLabelsHelper(organizationType, `charts.${screen}.average_elsa_score_title`))" :loading="Object.entries(avgElsaScore).length === 0">
          <template v-slot:body>
            <p class="opa-75 section-description">
              {{ $t($i18nLabelsHelper(organizationType, `charts.${screen}.average_elsa_score_desc`)) }}
            </p>
            <radial-bar-chart :series="avgElsaScore" />
          </template>
        </elsa-section>
      </el-col>
      <el-col class="box" :xs="24" :md="12">
        <elsa-section :title="$t($i18nLabelsHelper(currentOrganizationType, 'charts.distribution_by_learners_title'))" :loading="!distributionEps">
          <template v-slot:body>
            <p class="opa-75 section-description">
              {{ $t($i18nLabelsHelper(currentOrganizationType, 'charts.distribution_by_learners_desc')) }}
              <el-popover placement="bottom" trigger="hover" width="210" popper-class="distribution-eps-learn-more" v-model="hoverDitributionEpsLearnMoreBtn">
                <p id="distribution-eps-learn-more-desc">
                  {{ $t('charts.distribution_eps.learn_more.native') }}<br />
                  {{ $t('charts.distribution_eps.learn_more.advanced') }}<br />
                  {{ $t('charts.distribution_eps.learn_more.intermediate') }}<br />
                  {{ $t('charts.distribution_eps.learn_more.lower_intermediate') }}<br />
                  {{ $t('charts.distribution_eps.learn_more.beginner') }}<br />
                </p>
                <span slot="reference" id="distribution-eps-learn-more-btn" @mouseover="hoverDitributionEpsLearnMoreBtn = true" @mouseleave="hoverDitributionEpsLearnMoreBtn = false"> {{ $t('charts.learn_more') }} </span>
              </el-popover>
            </p>
            <distribution-of-learners-in-org-by-level-chart :chartData="distributionEps" />
          </template>
        </elsa-section>
      </el-col>
      <el-col class="box" :xs="24" :md="12" v-if="canShowPentagon">
        <elsa-section :title="$t($i18nLabelsHelper(organizationType, `charts.${screen}.pentagon_title`))">
          <template v-slot:body>
            <p class="opa-75 section-description">
              {{ $t($i18nLabelsHelper(organizationType, `charts.${screen}.pentagon_desc`)) }}
            </p>
            <pentagon :score="pentagon" />
          </template>
        </elsa-section>
      </el-col>
      <el-col class="box" :xs="24" :md="12">
        <elsa-section :title="$t($i18nLabelsHelper(organizationType, `charts.${screen}.ielts_title`))" :loading="!distributionIelts">
          <template v-slot:body>
            <p class="opa-75 section-description">
              {{ $t($i18nLabelsHelper(organizationType, `charts.${screen}.ielts_desc`)) }}
            </p>
            <div class="no-ielts-data" v-if="noIeltsData">
              <p class="head">
                {{ $t($i18nLabelsHelper(organizationType, `charts.${screen}.no_ielts_data_title`)) }}
              </p>
              <p class="sub">
                {{ $t($i18nLabelsHelper(organizationType, `charts.${screen}.no_ielts_data_desc`)) }}
              </p>
            </div>
            <distribution-of-learners-ielts-score-chart :series="distributionIeltsScores" :countOfLearners="countOfLearners"/>
          </template>
        </elsa-section>
      </el-col>
    </el-row>
    <el-row :gutter="35" class="info-boxes">
      <el-col :span="24" class="activity-reports">
        <section class="boxes-group-header">
          <h2>
            {{ $t('classes.classes_details.activity_reports.title') }}
          </h2>
          <div id="activity-reports-actions">
            <date-interval-picker :startDate="startDate" :endDate="endDate" :timelineLabel="timelineLabel" @updatedDate="setNewDate" @update:scope="updateScope" :enabledAction="enabledAction" />
            <class-excel-exporter :startDate="startDate" :canExport="canExport" :endDate="endDate" :isAdditionalScore="hasAdditionScore" />
            <organization-excel-exporter :startDate="startDate" :canExport="canExportOrgReport" :endDate="endDate" />
            <!-- <class-assessment-exporter :startDate="startDate" :canExport="canExport" :endDate="endDate" :isAdditionalScore="hasAdditionScore" /> -->
            <class-lesson-detail-exporter :startDate="startDate" :canExport="canExport" :endDate="endDate" />
          </div>
        </section>
      </el-col>
    </el-row>
    <el-row :gutter="35" class="info-boxes" type="flex">
      <el-col class="box" :xs="24" :md="12">
        <elsa-section :title="$t('classes.classes_details.activity_reports.lessons_overview')" :loading="Object.entries(overview).length === 0">
          <template v-slot:body>
            <div class="lessons-wrapper">
              <p class="label">
                {{ $t('common.total') }}
              </p>
              <div class="lesson-info">
                <div class="info">
                  <span>{{ sumActiveStudents }}</span>
                  <span>
                    {{ $t($i18nLabelsHelper(currentOrganizationType, 'active_students')) }}
                    <el-tooltip :content="$t('information.active_students')">
                      <i class="el-icon-info" />
                    </el-tooltip>
                  </span>
                </div>
                <div class="info">
                  <span>{{ totalLessons }}</span>
                  <span>
                    {{ $t('common.lessons') }}
                  </span>
                </div>
                <div class="info">
                  <span>{{ totalHours }}</span>
                  <span>
                    {{ $t('common.hours') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="lessons-wrapper">
              <p class="label">
                {{ $t('common.average') }}
              </p>
              <div class="lesson-info">
                <div class="info">
                  <span>{{ averageLessons }}</span>
                  <span>
                    {{ $t('common.lessons_per_day') }}
                  </span>
                </div>
                <div class="info">
                  <span>{{ averageTime }}</span>
                  <span>
                    {{ $t('common.minutes_per_day') }}
                  </span>
                </div>
              </div>
            </div>
          </template>
        </elsa-section>
      </el-col>
      <el-col class="box" :xs="24" :md="12">
        <elsa-section :title="$t('classes.classes_details.activity_reports.number_of_lessons_practiced')" icon="elsa-icon-chart-students" :loading="Object.entries(lessonsTotalTimePracticed).length === 0">
          <template v-slot:body>
            <mixed-chart :labels="dateLabels" :series="numberOfLessonsTotalTimePracticedSeries" :titleY="$t('common.lessons')" :titleYOpposite="$t('common.minutes')" />
          </template>
        </elsa-section>
      </el-col>
    </el-row>
    <el-row type="flex" :gutter="35" class="info-boxes">
      <el-col class="box" :xs="24" :md="12">
        <elsa-section :title="$t($i18nLabelsHelper(organizationType, 'average_lessons'))" icon="elsa-icon-chart-students" :loading="Object.entries(weeklyActiveStudents).length === 0">
          <template v-slot:body>
            <stacked-bar-chart :labels="weeklyDateLabels" :series="weeklyActiveStudents" />
          </template>
        </elsa-section>
      </el-col>
      <el-col class="box" :xs="24" :md="12">
        <elsa-section :title="$t('classes.classes_details.activity_reports.average_practice')" icon="elsa-icon-chart-students" :loading="Object.entries(avgPracticeTime).length === 0">
          <template v-slot:body>
            <!-- <bar-chart
              :series="avgPracticeTime"
              :labels="dateLabels"
              units="m"
            /> -->
            <mixed-chart :labels="dateLabels" :series="avgPracticeTimeSeries" :titleY="$t('common.minutes')" :titleYOpposite="$t('common.minutes')" />
          </template>
        </elsa-section>
      </el-col>
    </el-row>
    <el-row type="flex" :gutter="35" class="info-boxes">
      <el-col class="box" :xs="24" :md="12">
        <elsa-section :title="$t($i18nLabelsHelper(organizationType, 'classes.active_students'))" icon="elsa-icon-chart-students" :information="$t('information.active_students_chart')" :loading="Object.entries(activeStudents).length === 0">
          <template v-slot:body>
            <line-chart :series="activeStudents" :labels="dateLabels" />
          </template>
        </elsa-section>
      </el-col>
      <el-col class="box" :xs="24" :md="12">
        <elsa-section :title="$t('classes.classes_details.activity_reports.progress')" icon="elsa-icon-chart-students" :information="$t('information.activity_reports_progress')" :loading="Object.entries(eps).length === 0">
          <template v-slot:body>
            <line-chart :series="eps" :labels="dateLabels" />
          </template>
        </elsa-section>
      </el-col>
      <el-col class="box" :xs="24" :md="12" v-if="showStudentsImprovement">
        <elsa-section :title="$t($i18nLabelsHelper(organizationType, 'students_improvement'))" icon="elsa-icon-chart-students" :information="$t('information.students_improvement')" :loading="isLoadingImproChart">
          <template v-slot:body>
            <pie-chart :chartData="studentsImprovement" />
          </template>
        </elsa-section>
      </el-col>
    </el-row>
    <el-row type="flex" :gutter="35" class="info-boxes chart-section">
      <el-col class="box" :xs="24" :md="24">
        <elsa-section :title="$t($i18nLabelsHelper(organizationType, `charts.${screen}.average_elsa_score_historical_progress_title`))" :loading="Object.entries(eps).length === 0">
          <template v-slot:body>
            <p class="opa-75 section-description">
              {{ $t($i18nLabelsHelper(organizationType, `charts.${screen}.average_elsa_score_historical_progress_desc`)) }}
            </p>
            <average-elsa-score-progress-chart :labels="dateLabels" :series="averageElsaScoreHistoricalProgress" :titleY="$t('charts.average_elsa_score_historical_progress.titleY')" :titleYOpposite="$t('common.minutes')" />
          </template>
        </elsa-section>
      </el-col>
    </el-row>
    <el-row type="flex" :gutter="35" class="info-boxes chart-section">
      <el-col class="box" :xs="24" :md="24">
        <elsa-section :title="$t($i18nLabelsHelper(organizationType, `charts.${screen}.learning_effort_title`))">
          <template v-slot:body>
            <p class="opa-75 section-description">
              {{ $t($i18nLabelsHelper(organizationType, `charts.${screen}.learning_effort_decs`)) }}
            </p>
            <div class="flex">
              <el-tooltip :content="$t('charts.hours_practiced_tooltip')">
                <div class="effort-info-boxes flex items-center" id="hours-practiced">
                  <div class="icon" />
                  <div class="text-info">
                    <div>
                      <div class="flex items-center justify-between">
                        <div class="sum-label">{{ totalHours }}</div>
                        <div class="different-to-goal-label" :class="totalHours >= prevTotalHours ? 'success' : 'error'">
                          <b>{{ totalHours >= prevTotalHours ? '+' : '' }}{{ totalHours - prevTotalHours }} {{ Math.abs(totalHours - prevTotalHours) >= 2 ? 'hrs' : 'hr' }}</b> {{ $t('charts.from_last_period') }}
                        </div>
                      </div>
                    </div>
                    <div class="type-label upper">
                      {{ $t('charts.hours_practiced') }}
                    </div>
                  </div>
                </div>
              </el-tooltip>
              <div class="effort-info-boxes flex items-center" id="lessons-practiced">
                <div class="icon" />
                <div class="text-info">
                  <div>
                    <div class="flex items-center justify-between">
                      <div class="sum-label">{{ totalLessons }}</div>
                      <div class="different-to-goal-label" :class="totalLessons >= prevTotalLessons ? 'success' : 'error'">
                        <b>{{ totalLessons >= prevTotalLessons ? '+' : '' }}{{ totalLessons - prevTotalLessons }}</b> {{ $t('charts.from_last_period') }}
                      </div>
                    </div>
                  </div>
                  <div class="type-label upper">{{ $t('charts.lessons_practiced') }}</div>
                </div>
              </div>
            </div>
            <org-effort-chart :labels="dateLabels" :series="orgEfforts" :titleY="$t('common.minutes')" :titleYOpposite="$t('charts.number_of_lessons')" :goal="totalLessonsPracticedMinuteGoalInDuration" :showGoal="screen === 'group'" :organizationType="organizationType" />
            <!-- <div class="flex">
              <time-spent-chart 
                :labels="dateLabels" 
                :series="lessonsTimePracticedSeries" 
                :titleY="$t('common.minutes')"
                :titleYOpposite="$t('charts.time_spent.number_of_lessons')" 
                :information="$t('charts.time_spent.tooltip.lessons')"
                barColorStop1="#252259"
                barColorStop2="#8873EF"
              />
              <time-spent-chart 
                :labels="dateLabels" 
                :series="studySetTimePracticedSeries" 
                :titleY="$t('common.minutes')" 
                :titleYOpposite="$t('charts.time_spent.number_of_studysets')"
                :information="$t('charts.time_spent.tooltip.studysets')"
                barColorStop1="#713FF4"
                barColorStop2="#CD55FF"
              />
            </div> -->
          </template>
        </elsa-section>
      </el-col>
    </el-row>
    <el-row :gutter="35" class="info-boxes chart-section" type="flex">
      <el-col class="box" :xs="24" :md="12">
        <elsa-section :title="$t($i18nLabelsHelper(organizationType, `charts.${screen}.highest_average_score_skills_title`))" :loading="loadingAverageScoreSkills">
          <template v-slot:body>
            <div class="opa-75 section-description flex">
              <span class="flex content-center"> <i class="elsa-icon-chart-sound-game" /></span>
              <p class="score_skills_decs">{{ $t('charts.highest_average_score_skills_desc') }}</p>
            </div>
            <pronunciation-skill-list :series="highestAverageScoreSkills" />
          </template>
        </elsa-section>
      </el-col>
      <el-col class="box" :xs="24" :md="12">
        <elsa-section :title="$t('charts.lowest_average_score_skills_title')" :loading="loadingAverageScoreSkills">
          <template v-slot:body>
            <div class="opa-75 section-description flex">
              <span class="flex content-center"> <i class="elsa-icon-chart-sound-game" /></span>
              <p class="score_skills_decs">{{ $t('charts.lowest_average_score_skills_desc') }}</p>
            </div>
            <pronunciation-skill-list :series="lowestAverageScoreSkills" />
          </template>
        </elsa-section>
      </el-col>
    </el-row>
    <el-row type="flex" :gutter="35" class="info-boxes" id="leaderboard">
      <el-col class="box">
        <elsa-section :title="$t('charts.leaderboard')">
          <template v-slot:body>
            <el-tabs type="card" v-model="activeName" v-if="screen == 'organization'">
               <el-tab-pane v-if="hasChildOrg" :label="$t('charts.branchview_leaderboard_org_label')" name="branch" :isActive="activeName === 'branch'">
                <org-leaderboard-branch-view :new="false" :startDate="startDate" :endDate="endDate" />
              </el-tab-pane>
              <el-tab-pane :label="$t($i18nLabelsHelper(organizationType, `charts.teamview_leaderboard_org_label`))" name="team">
                <org-leaderboard-team-view :new="false" :startDate="startDate" :endDate="endDate" :isActive="activeName === 'team'" />
              </el-tab-pane>
              <el-tab-pane :label="$t('charts.individualview_leaderboard_org_label')" name="individual">
                <org-leaderboard-individual-view :new="false" :countIndividual="sumActiveStudents" :startDate="startDate" :endDate="endDate" :isActive="activeName === 'individual'" />
              </el-tab-pane>
            </el-tabs>
            <el-tabs type="card" v-model="groupActiveName" v-else>
              <el-tab-pane :label="$t('charts.individualview_leaderboard_org_label')" name="individual">
                <classes-leaderboard-individual-view :countIndividual="sumActiveStudents" :new="false" :startDate="startDate" :endDate="endDate" />
              </el-tab-pane>
            </el-tabs>
          </template>
        </elsa-section>
      </el-col>
    </el-row>
  </div>
</template>

<script>
// import BarChart from '@/components/common/charts/BarChart'
import StackedBarChart from '@/components/common/charts/StackedBarChart'
import MixedChart from '@/components/common/charts/MixedChart'
import LineChart from '@/components/common/charts/LineChart'
import PieChart from '@/components/common/charts/PieChart'
import RadialBarChart from '@/components/common/charts/RadialBarChart'
import DistributionOfLearnersInOrgByLevelChart from '@/components/common/charts/DistributionOfLearnersInOrgByLevelChart'
import Pentagon from '@/components/common/charts/Pentagon'
import ElsaSection from '@/components/common/ElsaSection'
import DateIntervalPicker from '@/components/common/DateIntervalPicker'
import ClassExcelExporter from '@/components/common/ClassExcelExporter'
// import ClassAssessmentExporter from '@/components/common/ClassAssessmentExporter'
import ClassLessonDetailExporter from '@/components/common/ClassLessonDetailExporter'
import { mapGetters } from 'vuex'
import { pathOr, compose } from 'ramda'
import { weeklyDateInterval, monthlyDateInterval, dailyDateInterval } from '@/utils/dateInterval'
import OrgLeaderboardIndividualView from './OrgLeaderboardIndividualView'
import OrgLeaderboardTeamView from './OrgLeaderboardTeamView'
import OrgLeaderboardBranchView from './OrgLeaderboardBranchView'
import ClassesLeaderboardIndividualView from './ClassesLeaderboardIndividualView'
import PronunciationSkillList from './PronunciationSkillList'
import OrgEffortChart from './OrgEffortChart'
// import TimeSpentChart from './TimeSpentChart'
import AverageElsaScoreProgressChart from './AverageElsaScoreProgressChart'
import DistributionOfLearnersIeltsScoreChart from '@/components/common/charts/DistributionOfLearnersIeltsScoreChart'
import OrganizationExcelExporter from '@/components/common/OrganizationExcelExporter'
import { PURPLE, PINK } from '@/constants/charts'

export default {
  components: {
    // BarChart,
    MixedChart,
    LineChart,
    ElsaSection,
    DateIntervalPicker,
    ClassExcelExporter,
    StackedBarChart,
    PieChart,
    // ClassAssessmentExporter,
    ClassLessonDetailExporter,
    RadialBarChart,
    DistributionOfLearnersInOrgByLevelChart,
    OrgLeaderboardIndividualView,
    OrgLeaderboardTeamView,
    OrgLeaderboardBranchView,
    ClassesLeaderboardIndividualView,
    Pentagon,
    PronunciationSkillList,
    OrgEffortChart,
    // TimeSpentChart,
    AverageElsaScoreProgressChart,
    DistributionOfLearnersIeltsScoreChart,
    OrganizationExcelExporter
  },
  data() {
    return {
      hoverDitributionEpsLearnMoreBtn: false,
      activeName: 'team',
      groupActiveName: 'individual',
      purple: PURPLE,
      pink: PINK
    }
  },
  props: {
    weeklyActiveStudents: {},
    activeStudents: {},
    avgPracticeTime: {},
    endDate: {},
    eps: {},
    organizationType: {},
    startDate: {},
    scope: {},
    timelineLabel: {},
    lessonsTotalTimePracticed: {
      type: Object,
      default: () => ({})
    },
    lessonsTimePracticed: {},
    studySetTimePracticed: {},
    overview: {},
    prevOverview: {},
    sumActiveStudents: {},
    studentsImprovement: {},
    showStudentsImprovement: {
      type: Boolean,
      default: false
    },
    isLoadingImproChart: {
      type: Boolean,
      default: false
    },
    avgElsaScore: {},
    distributionEps: {},
    distributionIelts: {},
    highestAverageScoreSkills: {
      type: Array,
      default: () => []
    },
    lowestAverageScoreSkills: {
      type: Array,
      default: () => []
    },
    loadingAverageScoreSkills: {
      type: Boolean,
      default: false
    },
    lessonsPracticedMinuteGoal: {
      type: Number,
      default: 0
    },
    totalLessonsPracticedMinuteGoalInDuration: {
      type: Number,
      default: 0
    },
    pentagon: {},
    screen: {
      type: String,
      default: 'group'
    },
    totalStudents: {
      type: Number,
      default: 0
    },
    hasAdditionScore: {
      type: Boolean,
      default: false
    },
    enabledAction: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    hasChildOrg(val) { 
      if(val) {
        this.activeName = "branch"
      } else {
        this.activeName = "team"
      }
    }
  },
  mounted () {
    if(this.hasChildOrg) {
      this.activeName = "branch"
    }
  },
  computed: {
    ...mapGetters('user', ['isElsaAdmin']),
    ...mapGetters({
      currentOrganizationType: 'organization/currentOrganizationType',
      userIsTeacher: 'organization/isOrganizationTeacher',
      userIsAdmin: 'organization/isOrganizationAdmin',
      isGeneralAdmin: 'organization/isGeneralAdmin',
      userIsAssociateAdmin: 'organization/isOrganizationAssociateAdmin',
      hasChildOrg: 'organization/hasChildOrg',
    }),
    weeklyDateLabels() {
      return weeklyDateInterval(this.startDate, this.endDate)
    },
    countOfLearners() {
      let students = {
          "no_score":0,
          "band_5":0,
          "band_6":0,
          "band_7":0,
          "band_8":0,
          "band_9":0
      }
      let totalLearners = 0
      let count = []
      if(this.distributionIelts != null) {
        for (const key in students) {
          students[key] = this.distributionIelts[key]?this.distributionIelts[key]:0
          totalLearners += students[key]
        }
        students['no_score'] = totalLearners==0?0:this.totalStudents - totalLearners
        count = Object.keys(students).map((key) => students[key])
      }
      else {
        count = Object.keys(students).map((key) => students[key])
      }
      return count
    },
    distributionIeltsScores() {
      let students = {
          "no_score":0,
          "band_5":0,
          "band_6":0,
          "band_7":0,
          "band_8":0,
          "band_9":0
      }
      let totalLearners = 0
      let series = []
      if(this.distributionIelts != null) {
        for (const key in students) {
          students[key] = this.distributionIelts[key]?this.distributionIelts[key]:0
          totalLearners += students[key]
        }
        students['no_score'] = totalLearners==0?0:this.totalStudents - totalLearners
        const items = Object.keys(students).map((key) => students[key])
        series = [{
          'data': items.map(function(element){
          if(element != 0)
            return (Math.round(element * 100) / items.reduce((a, b) => a + b, 0)).toFixed(0)
              else
            return 0
          })
        }]
      }
      else {
        series = [{
          'data': Object.keys(students).map((key) => students[key])
        }]
      }
      return series
    },
    dateLabels() {
      if (this.scope === 'weekly') {
        return weeklyDateInterval(this.startDate, this.endDate)
      } else if (this.scope === 'monthly') {
        return monthlyDateInterval(this.startDate, this.endDate)
      }

      return dailyDateInterval(this.startDate, this.endDate)
    },
    numberOfLessonsTotalTimePracticedSeries() {
      const numberOfLessons = pathOr([], ['lessonsTotalTimePracticed', 'numberOfLessons'])(this)
      const numberOfMinutes = pathOr([], ['lessonsTotalTimePracticed', 'numberOfMinutes'])(this)
      return [
        {
          name: 'number_of_seconds',
          type: 'line',
          data: numberOfMinutes
        },
        {
          name: 'number_of_lessons',
          type: 'column',
          data: numberOfLessons
        }
      ]
    },
    // lessonsTimePracticedSeries() {
    //   const numberOfLessons = pathOr([], ['lessonsTimePracticed', 'numberOfLessons'])(this)
    //   const numberOfMinutes = pathOr([], ['lessonsTimePracticed', 'numberOfMinutes'])(this)
    //   return [
    //     {
    //       name: 'number_of_seconds',
    //       type: 'line',
    //       data: numberOfMinutes
    //     },
    //     {
    //       name: 'number_of_lessons',
    //       type: 'column',
    //       data: numberOfLessons
    //     }
    //   ]
    // },
    // studySetTimePracticedSeries() {
    //   const numberOfLessons = pathOr([], ['studySetTimePracticed', 'numberOfLessons'])(this)
    //   const numberOfMinutes = pathOr([], ['studySetTimePracticed', 'numberOfMinutes'])(this)
    //   return [
    //     {
    //       name: 'number_of_seconds',
    //       type: 'line',
    //       data: numberOfMinutes
    //     },
    //     {
    //       name: 'number_of_studysets',
    //       type: 'column',
    //       data: numberOfLessons
    //     }
    //   ]
    // },
    orgEfforts() {
      const numberOfLessons = pathOr([], ['lessonsTotalTimePracticed', 'numberOfLessons'])(this)
      const numberOfMinutes = pathOr([], ['lessonsTotalTimePracticed', 'numberOfMinutes'])(this)
      return [
        {
          name: 'minutes_practiced',
          type: 'line',
          data: numberOfMinutes
        },
        {
          name: 'lessones_practices',
          type: 'column',
          data: numberOfLessons
        }
      ]
    },
    averageElsaScoreHistoricalProgress() {
      const numberOfMinutes = pathOr([], ['lessonsTotalTimePracticed', 'numberOfMinutes'])(this)

      let progress = {
        name: 'avg_eps_full',
        type: 'line',
        data: []
      }
      if (this.eps && this.eps.length > 0) {
        progress = { ...this.eps[0], type: 'line', name: 'avg_eps_full' }
      }
      return [
        progress,
        {
          name: 'minutes_practiced',
          type: 'column',
          data: numberOfMinutes
        }
      ]
    },
    avgPracticeTimeSeries() {
      const avgPracticeTime = pathOr([], ['avgPracticeTime', 'avgPracticeTime'])(this)
      const avgPracticeTimePerday = pathOr([], ['avgPracticeTime', 'avgPracticePerday'])(this)
      return [
        {
          name: 'avg_practice_time',
          type: 'column',
          data: avgPracticeTime
        },
        {
          name: 'avg_practicetime_perday',
          type: 'line',
          data: avgPracticeTimePerday
        }
      ]
    },
    totalLessons: pathOr(0, ['overview', 'lessons_count']),
    prevTotalLessons: pathOr(0, ['prevOverview', 'lessons_count']),
    totalHours: compose(Math.round, pathOr(0, ['overview', 'hours'])),
    prevTotalHours: compose(Math.round, pathOr(0, ['prevOverview', 'hours'])),
    averageLessons: compose(Math.round, pathOr(0, ['overview', 'lessons_per_day'])),
    averageTime: compose(Math.round, pathOr(0, ['overview', 'minutes_per_day'])),
    canShowPentagon() {
      const isShow = this.pentagon && this.pentagon.pronunciation >= 0 && this.pentagon.fluency >= 0 && this.pentagon.intonation >= 0 && this.pentagon.word_stress >= 0 && this.pentagon.listening >= 0
      return isShow
    },
    canExport() {
      return this.$route.name == 'class-details' && (this.userIsAdmin || this.userIsAssociateAdmin || this.userIsTeacher)
    },
    canExportOrgReport() {
      return this.$route.name == 'dashboard' && (this.isGeneralAdmin || this.userIsAdmin || this.userIsAssociateAdmin);
    },
    noIeltsData() {
      if(this.distributionIelts != null) {
          if(Object.keys(this.distributionIelts).length === 0 || this.distributionIelts[0]) 
            return true;
          else
            return false
      }
      else {
        return true
      }
    }
  },
  methods: {
    setNewDate(dateObject) {
      return this.$emit('updatedDate', dateObject)
    },
    updateScope(newScope) {
      return this.$emit('updateScope', newScope)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../scss/_colors.scss';

.activity-reports {
  margin-top: 32px;
  display: flex;
  align-items: center;

  h3 {
    color: $purple;
  }
  .date-interval-picker-section {
    padding: 0 15px;
  }
}

.info-boxes {
  .box {
    //margin-top: 32px;
    overflow: hidden;
    & > div {
      margin-top: 32px;
    }
  }
}

.elsa-section {
  padding-bottom: 32px;
  height: 100%;
  &::v-deep .section-body {
    margin-top: 32px;
  }

  &::v-deep .section-title {
    font-size: 24px;
    font-weight: 600;
    line-height: 36px;
    color: #494955;
  }

  &::v-deep h3 {
    text-transform: none;
  }
}
.lessons-wrapper {
  &:first-of-type {
    padding-bottom: 24px;
    border-bottom: 1px solid $light-blue;
  }
  &:last-of-type {
    padding-top: 24px;
  }
  p.label {
    font-size: 14px;
    text-transform: uppercase;
    color: $dark-blue-2;
    padding-bottom: 5px;
    margin: 0;
  }
  .lesson-info {
    display: flex;
    flex-wrap: wrap;

    .info {
      flex: 1;
      display: flex;
      flex-direction: column;
      margin-bottom: 5px;
      &:not(:first-child) {
        margin-left: 5px;
      }
      &:not(:last-child) {
        margin-right: 5px;
      }

      @media only screen and (max-width: 768px) {
        margin-bottom: 15px;
      }
      span:first-of-type {
        font-weight: bold;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: $darker-blue;
        font-size: 28px;
        mix-blend-mode: normal;
      }
      span {
        font-size: 16px;
        text-transform: capitalize;
        color: $darker-blue;
      }
    }
  }
}
.opa-75 {
  opacity: 0.75;
}

.no-ielts-data {
  text-align: center;
  padding: 0 50px;
  position: relative;
  top: 100px;
  .head {
    font-size: 22px;
    line-height: 24px;
    font-weight: 700;
    color: rgba(73, 73, 85, 0.6);
  }
  .sub {
    font-size: 16px;
    line-height: 24px;
    font-weight: 400;
    color: rgba(91, 91, 91, 0.5);
  }
}

#leaderboard::v-deep {
  .el-tabs__nav-wrap {
    background-color: #f1f3f5;
    padding-top: 16px;
    margin-top: 16px;
    border: none;
  }

  .el-tabs__nav-scroll {
    border: none;
  }

  .el-tabs__nav {
    border-top: none;
    border-right: none;
    border-left: none;
  }
  .el-tabs__item {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    background-color: #dee2e6;
    &.is-active {
      background-color: #fff;
    }
    margin-right: 8px;

    &:first-child {
      margin-left: 22px;
    }

    &:last-child {
      margin-right: 0px;
    }
  }

  .el-tabs__header {
    margin-bottom: 0;
  }
}
::v-deep #distribution-eps-learn-more {
  margin-top: 10px;
  transform: translateY(-10px);
}

#distribution-eps-learn-more-btn {
  padding-bottom: 16px;

  color: #007eef;

  &:hover {
    text-decoration: underline;
    color: #0055df;
  }
}

#distribution-eps-learn-more-desc {
  margin-top: 0px;
  margin-bottom: 0px;
  line-height: 20px;
  size: 14px;
  font-weight: 400;
  color: #141417;
  font-family: 'Roboto', 'SF Pro Display', sans-serif;
}
.boxes-group-header {
  padding-left: 32px;
  padding-right: 32px;
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;

  h2 {
    font-weight: 700;
    font-size: 32px;
    line-height: 48px;
    // text-transform: none;
  }
}

@media only screen and (max-width: 768px) {
  .boxes-group-header {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }
}

.chart-section {
  &::v-deep .elsa-section {
    padding-bottom: 0px;
    .section-body {
      margin-top: 0px;
    }
  }
}

.section-description {
  font-size: 18px;
  line-height: 28px;
  font-weight: 400;
  margin-top: 8px;
}

#activity-reports-actions {
  display: flex;
  justify-content: flex-end;
}

.effort-info-boxes {
  height: 110px;
  padding-left: 16px;
  padding-right: 16px;
  border: 1.5px solid rgba(91, 91, 91, 0.3);
  box-sizing: border-box;
  border-radius: 8px;
  margin-right: 16px;

  :last-child {
    margin-right: 0px;
  }

  .icon {
    content: '';
    display: block;
    background-repeat: no-repeat;
    height: 56px;
    width: 56px;
    margin-right: 16px;
    background-color: transparent;
    background-size: contain;
  }

  .different-to-goal-label {
    padding: 4px 16px;

    border-radius: 20px;
    font-size: 14px;

    height: 20px;

    &.success {
      background: rgba(76, 217, 100, 0.3);
      color: $success;
    }

    &.error {
      background: $error;
      color: $white;
    }
  }

  .sum-label {
    font-size: 40px;
    line-height: 60px;
    font-weight: 500;
    margin-right: 24px;
  }

  .type-label {
    font-size: 16px;
    font-weight: 700;
    line-height: 25px;
  }
}

#hours-practiced {
  color: #f48f4d;
  .icon {
    background-image: url('~@/assets/images/chart__clock.svg');
  }
}

#lessons-practiced {
  color: #478dff;
  .icon {
    background-image: url('~@/assets/images/chart__book-library.svg');
  }
}

.score_skills_decs {
  font-weight: 600;
  font-size: 16px;
  line-height: 16px;
}
</style>
